<template>
<section class="offers static-container" :style="{
  '--image-aspect-ratio': settings.image_aspect_ratio_width / settings.image_aspect_ratio_height,
}">
  <header class="offers-header" v-if="hasTitle || hasSubtitle">
    <h2 class="offers-title" v-if="hasTitle">{{ title }}</h2>
    <h4 class="offers-subtitle" v-if="hasSubtitle">{{ subtitle }}</h4>
  </header>
  <div class="offers-list swiper-container" ref="swiperContainer">
    <div class="swiper-wrapper">
      <!-- Offer cards come by groups of 1-3 items per slide, depending on screen size. -->
      <div v-for="offersGroup in swiperData.slides"
           :key="offersGroup[0].id"
           :style="{ left: `${swiperData.offset}px` }"
           class="offers-group swiper-slide"
      >
        <offer-card v-for="offer in offersGroup"
                    :key="`offer-card-${offer.id}`"
                    :offer="offer"
        />
      </div>
    </div>

    <div class="swiper-pagination" ref="swiperPagination"></div>

    <div class="navigation-buttons-wrapper">
      <div class="swiper-button-prev" ref="swiperButtonPrev">
        <img :src="require('@/assets/images/offers/arrow-right-encircled.svg')"
             style="transform: rotate(0.5turn);"
             alt=""
        >
      </div>
      <div class="spacer" :class="{ [`num-cards-${currOffersGroupSize}`]: true }"></div>
      <div class="swiper-button-next" ref="swiperButtonNext">
        <img :src="require('@/assets/images/offers/arrow-right-encircled.svg')" alt="">
      </div>
    </div>
  </div>
</section>
</template>

<script>
import { mapGetters } from 'vuex'
import { Swiper, Pagination, Navigation, Virtual } from 'swiper/js/swiper.esm';
import debounce from 'lodash/debounce';
import OfferCard from '@/components/offers/OfferCard'

Swiper.use([Pagination, Navigation, Virtual]);

export default {
  components: {
    OfferCard,
  },

  mounted() {
    const componentInstance = this;
    this.swiper = new Swiper(this.$refs.swiperContainer, {
      init: false,
      direction: 'horizontal',
      pagination: {
        el: this.$refs.swiperPagination,
        clickable: true,
      },
      navigation: {
        prevEl: this.$refs.swiperButtonPrev,
        nextEl: this.$refs.swiperButtonNext,
      },
      virtual: {
        slides: [],
        renderExternal(data) {
          componentInstance.swiperData = data;
        },
      },
      slidesPerView: 1,
    });
    this.regroupOffers();
    this.swiper.init();

    window.addEventListener('resize', this.debounceRegroupOffers);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.debounceRegroupOffers);
  },

  computed: {
    ...mapGetters({
      settings: 'settings/homeOffers',
      offers: 'offers/offers',
    }),

    hasTitle() {
      return !!this.title
    },

    hasSubtitle() {
      return !!this.subtitle
    },

    title() {
      return this.settings.title
    },

    subtitle() {
      return this.settings.subtitle
    },
  },

  methods: {
    regroupOffers() {
      function group(array, groupSize) {
        const groups = [];
        const arrayCopy = [...array];
        while (arrayCopy.length > 0) {
          groups.push(arrayCopy.splice(0, groupSize));
        }
        return groups;
      }

      // WET! Change `@max-num-cards` in `offers.less` if you're going to increase the number of cards.
      let newGroupSize = 1;
      if (window.innerWidth > 1000) {
        newGroupSize = 2;
      }
      if (window.innerWidth > 1300) {
        newGroupSize = 3;
      }
      if (this.currOffersGroupSize === newGroupSize) {
        return;
      }
      this.currOffersGroupSize = newGroupSize;
      const groups = group(this.offers, newGroupSize);

      this.swiper.virtual.removeAllSlides();
      this.swiper.virtual.appendSlide(groups);
      this.swiper.update();
    }
  },

  data() {
    return {
      swiper: null,
      swiperData: {
        slides: [],
      },
      currOffersGroupSize: undefined,
      debounceRegroupOffers: debounce(this.regroupOffers, 50),
    }
  }
}
</script>

<style lang="less">
@import '~swiper/css/swiper.min.css';

.offers {
  @apply pt-1 pb-px;
  .offers-header {
    @apply hidden;
    @apply text-center;
    @apply mb-10;
    .offers-title {
      @apply text-3xl font-normal my-3;
    }

    .offers-subtitle {
      @apply text-lg font-light mb-8;
    }
  }

  .offers-list {
    @apply mt-3;
    @card-width-px: 310;
    @card-width: @card-width-px * 1px;
    @border-width: 1px;
    @image-height: calc(@card-width-px / var(--image-aspect-ratio) * 1px - @border-width * 2);
    @content-height: 7rem;
    @card-height: calc(@image-height + @content-height + @border-width * 2);
    @card-margin-x: theme('margin.2');
    .offers-group {
      @apply flex flex-row justify-center px-2;
      .offer-item {
        @apply bg-white rounded
          overflow-hidden; // So the image doesn't overflow border corners.
        border-width: @border-width;
        width: @card-width;
        margin: 0 @card-margin-x;
        box-sizing: border-box;

        .offer-image {
          height: @image-height;
          @apply flex items-center justify-center;

          img {
            object-fit: cover;
            @apply h-full w-full;
          }
        }

        .offer-content {
          // We can't set height directly on the clamped element as clamped text would show up, hence the container.
          height: @content-height;
          @apply pt-2 px-3 border-t;
          @apply pb-3; // So if text is truncated, half of the truncated line is not visible.
          line-height: 1.15; // Adjusted so the text best fits with no heading, one-row heading, and two-row heading.
          .text-container {
            @apply text-left h-full overflow-y-hidden;
            .offer-title {
              @apply text-base font-normal text-left mb-1;
            }

            p {
              @apply inline font-light text-sm opacity-60;
            }
          }
        }
      }
    }
    .navigation-buttons-wrapper {
      @apply invisible absolute inset-x-0 top-0 flex flex-row;
      .spacer {
        // Hackily position navigation buttons horizontally in the center between cards and offers section borders.
        @apply pointer-events-none;
        @max-num-cards: 3; // WET! See Offers.vue, `newGroupSize`.
        .num-cards-loop (@i) when (@i > 0) {
            &.num-cards-@{i} {
                width: calc(@i * @card-width + (@i - 1) * @card-margin-x * 2);
            }
            .num-cards-loop(@i - 1);
        }
        .num-cards-loop (@max-num-cards);
      }
      .swiper-button-prev, .swiper-button-next {
        @apply static h-full flex-grow mt-auto;
        height: @card-height;
        &:after {
          // Override default icon.
          @apply hidden;
        }
      }
    }

    .swiper-pagination {
      @apply static my-4;
      font-size: 0;
      .swiper-pagination-bullet {
        @apply opacity-100 w-3 h-3 bg-transparent border-2 border-primary;
        &.swiper-pagination-bullet-active {
          @apply bg-primary;
          box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.5);
        }
      }
    }
  }
}

@screen sm {
  .offers {
    .offers-header {
      @apply block;
    }
  }
}

@screen md {
  .offers {
    .offers-list {
      .navigation-buttons-wrapper {
        @apply visible;
      }
      .swiper-pagination {
        @apply my-10;
      }
    }
  }
}
</style>
